import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

const SESSION_KEY = 'mateo_last_test_build';

/**
 * Runs automated tests ONCE per deployment / code change.
 * Compares the current build timestamp (injected by Vite) against
 * the last-tested value stored in sessionStorage.
 * Also triggers if new users were added since the last run.
 */
export const useAutoTestRunner = () => {
  const isRunningRef = useRef(false);

  useEffect(() => {
    const BUILD_ID = import.meta.env.VITE_BUILD_TIMESTAMP ?? '__dev__';

    const runTests = async (reason: string) => {
      if (isRunningRef.current) return;
      try {
        isRunningRef.current = true;
        console.log(`[AutoTest] Running tests – reason: ${reason}`);

        const { data, error } = await supabase.functions.invoke('run-automated-tests', {
          body: { triggered_by: reason },
        });

        if (error) {
          console.error('[AutoTest] Error:', error);
        } else {
          console.log('[AutoTest] Completed:', data?.summary || 'No summary');
        }

        // Mark this build as tested
        try { sessionStorage.setItem(SESSION_KEY, BUILD_ID); } catch {}
      } catch (err) {
        console.error('[AutoTest] Exception:', err);
      } finally {
        isRunningRef.current = false;
      }
    };

    const checkIsAdmin = async (userId: string): Promise<boolean> => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', userId)
        .single();

      const adminEmails = ['paul.fouad@gomateo.io', 'admin@gomateo.io'];
      return adminEmails.includes(profile?.email || '');
    };

    const shouldRunForBuild = (): boolean => {
      try {
        return sessionStorage.getItem(SESSION_KEY) !== BUILD_ID;
      } catch {
        return true;
      }
    };

    const checkNewUsers = async (): Promise<boolean> => {
      // Check if any user signed up in the last 30 minutes
      const since = new Date(Date.now() - 30 * 60 * 1000).toISOString();
      const { count } = await supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true })
        .gte('created_at', since);
      return (count ?? 0) > 0;
    };

    const setup = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const isAdmin = await checkIsAdmin(session.user.id);
      if (!isAdmin) return;

      // Run if build changed (new deployment)
      if (shouldRunForBuild()) {
        runTests('new_deployment');
        return;
      }

      // Run if new users joined recently
      const hasNewUsers = await checkNewUsers();
      if (hasNewUsers) {
        runTests('new_users');
      }
    };

    setup();
  }, []);
};
